import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import {
  Equation,
  ExpressionType,
  LogTemplateType,
  LogsTemplateField,
  SectionUnitsEnum,
} from '@/__generated__/graphql';
import { keyFromTitle } from '../../utils';
import { EquationCard } from './Equations/EquationCard';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { generateMongoObjectId } from '@/utils/databaseHelpers';

const EMPTY_FIELD_KEY_SET = new Set<string>();

type EquationsConfigTabProps = {
  mode: 'ReadOnly' | 'Edit';
  equations: Equation[];
  availableFieldKeys: string[];
  availableFields?: LogsTemplateField[];
  /** Keys that belong to the Indirect POB logs template (persisted on equation variables). */
  indirectPobFieldKeys?: ReadonlySet<string>;
  /** Keys that belong to the work log template (persisted on equation variables). */
  worklogFieldKeys?: ReadonlySet<string>;
  /** Keys that belong to the cost log template (persisted on equation variables). */
  costLogFieldKeys?: ReadonlySet<string>;
  /** Keys that belong to the QA/QC logs template (persisted on equation variables). */
  qaqcFieldKeys?: ReadonlySet<string>;
  hostLogTemplateType: LogTemplateType;
  onEquationsChange: (equations: Equation[]) => void;
  onCreateNewField?: (
    fieldKey: string,
    equationKey: string,
    config?: { fieldUnit?: SectionUnitsEnum | null; displayInLogAggregation?: boolean }
  ) => void;
  onEquationDerivedFieldConfigChange?: (
    equationIndex: number,
    config: { fieldUnit: SectionUnitsEnum | null; displayInLogAggregation: boolean }
  ) => void;
  originalEquations?: Equation[];
};

export const EquationsConfigTab = ({
  mode,
  equations,
  availableFieldKeys,
  availableFields = [],
  indirectPobFieldKeys = EMPTY_FIELD_KEY_SET,
  worklogFieldKeys = EMPTY_FIELD_KEY_SET,
  costLogFieldKeys = EMPTY_FIELD_KEY_SET,
  qaqcFieldKeys = EMPTY_FIELD_KEY_SET,
  hostLogTemplateType,
  onEquationsChange,
  onCreateNewField,
  onEquationDerivedFieldConfigChange,
  originalEquations = [],
}: EquationsConfigTabProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [equationToDeleteIndex, setEquationToDeleteIndex] = useState<number | null>(null);

  const [equationUiKeys, setEquationUiKeys] = useState<string[]>([]);
  const [equationIsNewFlags, setEquationIsNewFlags] = useState<boolean[]>([]);

  useEffect(() => {
    if (equations.length === 0) {
      setEquationUiKeys([]);
      setEquationIsNewFlags([]);
      return;
    }

    if (
      equationUiKeys.length === equations.length &&
      equationIsNewFlags.length === equations.length
    ) {
      return;
    }

    const originalKeySet = new Set(
      originalEquations.map((equation) => equation.key).filter(Boolean)
    );
    setEquationUiKeys(equations.map(() => generateMongoObjectId()));
    setEquationIsNewFlags(equations.map((equation) => !originalKeySet.has(equation.key ?? '')));
  }, [equations, equationUiKeys.length, equationIsNewFlags.length, originalEquations]);

  const equationKeyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const equation of equations) {
      const key = equation.key?.trim();
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [equations]);

  const addNewEquation = useCallback(() => {
    const name = 'New Equation';
    const newEquation: Equation = {
      key: keyFromTitle(name),
      name,
      variables: [],
      expression: {
        type: ExpressionType.Number,
        value: 0,
      } as Equation['expression'],
    };
    setEquationUiKeys([...equationUiKeys, generateMongoObjectId()]);
    setEquationIsNewFlags([...equationIsNewFlags, true]);
    onEquationsChange([...equations, newEquation]);
  }, [equations, equationUiKeys, equationIsNewFlags, onEquationsChange]);

  const updateEquation = useCallback(
    (index: number, updates: Partial<Equation>) => {
      const updatedEquations = equations.map((equation, i) =>
        i === index ? { ...equation, ...updates } : equation
      );
      onEquationsChange(updatedEquations);
    },
    [equations, onEquationsChange]
  );

  const deleteEquation = useCallback(
    (index: number) => {
      const updatedEquations = equations.filter((_, i) => i !== index);
      setEquationUiKeys(equationUiKeys.filter((_, i) => i !== index));
      setEquationIsNewFlags(equationIsNewFlags.filter((_, i) => i !== index));
      onEquationsChange(updatedEquations);
    },
    [equations, equationUiKeys, equationIsNewFlags, onEquationsChange]
  );

  return (
    <Box p={3}>
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight={600}>
          Equations Configuration
        </Typography>

        {mode === 'Edit' && (
          <Button
            startIcon={<Add />}
            variant="contained"
            onClick={addNewEquation}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add New Equation
          </Button>
        )}

        {equations.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No equations configured. {mode === 'Edit' && 'Click "Add New Equation" to create one.'}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {equations.map((equation, index) => (
              <EquationCard
                key={equationUiKeys[index] ?? `equation-ui-${index}`}
                equation={equation}
                index={index}
                equationUiKey={equationUiKeys[index] ?? `equation-ui-${index}`}
                mode={mode}
                availableFieldKeys={availableFieldKeys}
                availableFields={availableFields}
                indirectPobFieldKeys={indirectPobFieldKeys}
                worklogFieldKeys={worklogFieldKeys}
                costLogFieldKeys={costLogFieldKeys}
                qaqcFieldKeys={qaqcFieldKeys}
                hostLogTemplateType={hostLogTemplateType}
                isNewEquation={equationIsNewFlags[index] ?? true}
                hasDuplicateKey={(equationKeyCounts.get(equation.key?.trim() ?? '') ?? 0) > 1}
                onEquationDerivedFieldConfigChange={onEquationDerivedFieldConfigChange}
                onUpdate={(updates: Partial<Equation>) => updateEquation(index, updates)}
                onDelete={() => {
                  setEquationToDeleteIndex(index);
                  setIsDeleteDialogOpen(true);
                }}
                onCreateNewField={onCreateNewField}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setEquationToDeleteIndex(null);
          }}
          title="Delete Equation"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this equation? Deleting this equation will cause the
              linked cards to be deleted as well.
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setEquationToDeleteIndex(null);
                }}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (equationToDeleteIndex !== null) {
                    deleteEquation(equationToDeleteIndex);
                    setEquationToDeleteIndex(null);
                    setIsDeleteDialogOpen(false);
                  }
                }}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </Box>
  );
};
